import React, { useContext } from 'react'
import { Link } from 'react-router-dom'
import { Button, Image } from '@deal/components'
import { DepartmentType, HeroCuratedExpertFragment } from '#src/generated/graphql'
import EmptyCurations from '../EmptyCurations'
import RightProgress from '../RightProgress'
import Check from './check.svg'
import { SellableWithImage, CategoryItemsBadge } from '..'
import styles from './styles.css'
import { IdentityContext } from '#src/containers/Identity'

interface Props {
  categories: CategoryItemsBadge[]
  itemsSellables: SellableWithImage[]
  onSubmit: () => void
  messageCount: number
  itemCount: number
  banner?: string | null
  expert: HeroCuratedExpertFragment
  recommendationsLink?: string
  conversationId: string
  departmentType?: DepartmentType | null
}

interface CurationBodyProps {
  departmentType?: DepartmentType | null
  expertFirstName?: string | null
}

const CurationBody: React.SFC<CurationBodyProps> = ({ departmentType, expertFirstName }) => {
  switch (departmentType) {
    case DepartmentType.GOLF:
      return (
        <>
          Check out what {expertFirstName ? expertFirstName : 'we'} recommended for you, and tell
          them what you think. They’ll continue working with you until you find the right clubs.
        </>
      )

    case DepartmentType.YACHT_CHARTERS:
      return (
        <>
          Check out what {expertFirstName ? expertFirstName : 'we'} recommended for you, and tell
          them what you think. They’ll continue working with you until you plan the perfect trip.
        </>
      )

    case DepartmentType.CYCLING:
      return (
        <>
          Check out what {expertFirstName ? expertFirstName : 'we'} recommended for you, and tell
          them what you think. They’ll continue working with you until you find the right bike.
        </>
      )

    case DepartmentType.WINTER_SPORTS:
      return (
        <>
          Check out what {expertFirstName ? expertFirstName : 'we'} recommended for you, and tell
          them what you think. They’ll continue working with you until you find the right gear.
        </>
      )

    case DepartmentType.CAMPING:
      return (
        <>
          Your gear recommendations from {expertFirstName ? expertFirstName : 'us'} are waiting, let
          {expertFirstName ? 'them' : 'us'} know what you think.{' '}
          {expertFirstName ? "They'll" : "We'll"} continue working with you until you find the gear
          perfect for your adventure.
        </>
      )

    default:
      return (
        <>
          Check out what {expertFirstName ? expertFirstName : 'we'} recommended for you, and tell
          them what you think. They’ll continue working with you until you find the right items.
        </>
      )
  }
}

const StatusBar: React.SFC<Props> = ({
  categories,
  itemsSellables,
  expert,
  onSubmit,
  messageCount,
  itemCount,
  banner,
  recommendationsLink,
  conversationId,
  departmentType
}) => {
  const { myself } = useContext(IdentityContext)

  return (
    <RightProgress
      messageCount={messageCount}
      itemCount={itemCount}
      banner={banner}
      expertFirstName={expert.shortName}
      isPlaceholderExpert={expert.expertAttributes && expert.expertAttributes.placeholderExpert}
    >
      {!itemsSellables.length || !recommendationsLink ? (
        <EmptyCurations
          onSubmit={onSubmit}
          departmentType={departmentType}
          conversationId={conversationId}
          expertFirstName={expert.shortName}
          isPlaceholderExpert={expert.expertAttributes && expert.expertAttributes.placeholderExpert}
        />
      ) : (
        <>
          <div className={styles.flex}>
            <div className={styles.bullets}>
              <span className={styles.bulletActive}>
                <Check className={styles.check} width={10} />
              </span>
              <span className={styles.separator} />
              <span className={styles.bulletActive}>
                <Check className={styles.check} width={10} />
              </span>
              <span className={styles.separator} />
              <span className={styles.bulletCurrent} />
            </div>
            <div className={styles.smallTitle}>shop your picks</div>
          </div>
          <div className={styles.title}>
            {myself ? `Welcome back, ${myself.user.firstName}!` : 'Welcome back!'}
          </div>
          <div className={styles.subTitle}>
            <CurationBody departmentType={departmentType} expertFirstName={expert.shortName} />
          </div>
          <div className={styles.itemsContainer}>
            <div className={styles.itemsSubContainer}>
              {categories.map(({ categoryName, itemsNumber }) => (
                <div key={categoryName} className={styles.recommendationCategory}>
                  {itemsNumber} {categoryName.toLowerCase()}{' '}
                  {itemsNumber === 1 ? 'option' : 'options'}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.footer}>
            <Link to={recommendationsLink} className={styles.link}>
              <Button onClick={onSubmit} className={styles.button}>
                See curation
              </Button>
            </Link>
            <div className={styles.curatedContainer}>
              {expert.expertProfile && expert.expertProfile.profilePhoto && (
                <Image
                  src={expert.expertProfile.profilePhoto.url}
                  width={30}
                  height={30}
                  className={styles.curatedImage}
                  htmlAttributes={{
                    alt: `${expert.displayName}  on Curated.com`
                  }}
                />
              )}
              <div className={styles.curatedText}>
                Curated by <strong className={styles.expertName}>{expert.displayName} </strong> for
                you
              </div>
            </div>
          </div>
        </>
      )}
    </RightProgress>
  )
}

export default StatusBar
